import React, { useContext, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import mapboxgl from "mapbox-gl";

import { makeVersionVectorTileURL, orderLayersByConfiguration } from "fond/api";
import { BASE_MAP_CUSTOM_OVERLAY_MARKER } from "fond/constants";
import SourceAndLayers from "fond/map/SourceAndLayers";
import { getCurrentProject } from "fond/project";
import { GroupConfig, Store } from "fond/types";
import { GeoreferencedImageItem } from "fond/types/overlays";
import { LayerStyle } from "fond/types/ProjectLayerConfig";
import { isVisible } from "fond/utils/configurations";

import { useOverlaysWithConfigs } from "./Overlays/useOverlaysWithConfigs";
import { MapContext } from "./MapProvider";
import { useStyleEpoch } from "./useStyleEpoch";

interface IProps {
  layerView: {
    [key: string]: boolean;
  };
}

/**
 * Renders a single georeferenced image on the map as a Mapbox image source + raster layer.
 */
const GeorefImageLayer: React.FC<{ image: GeoreferencedImageItem; visible: boolean }> = ({ image, visible }) => {
  const { map } = useContext(MapContext);
  const addedRef = useRef(false);
  const styleEpoch = useStyleEpoch(map);

  const sourceId = `georef-image-source-${image.ID}`;
  const layerId = `georef-image-layer-${image.ID}`;

  // Add or update the image source and layer
  useEffect(() => {
    if (!map) return;

    const source = map.getSource(sourceId) as mapboxgl.ImageSource | undefined;
    if (source) {
      source.updateImage({ url: image.ImageUrl, coordinates: image.ImageCorners });
    } else {
      map.addSource(sourceId, {
        type: "image",
        url: image.ImageUrl,
        coordinates: image.ImageCorners,
      });
      map.addLayer(
        {
          id: layerId,
          type: "raster",
          source: sourceId,
          paint: {
            "raster-opacity": image.Opacity,
          },
        },
        BASE_MAP_CUSTOM_OVERLAY_MARKER
      );
      addedRef.current = true;
    }
  }, [map, sourceId, layerId, image.ImageUrl, image.ImageCorners, image.Opacity, styleEpoch]);

  // Update visibility
  useEffect(() => {
    if (map?.getLayer(layerId)) {
      map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
    }
  }, [map, layerId, visible]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (!map) return;
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    };
  }, [map, sourceId, layerId]);

  return null;
};

/**
 * Component responsible for rendering custom overlay layers on the map.
 * This component:
 * 1. Loads all custom overlays for the current version/project
 * 2. For each overlay, fetches its source version's configuration
 * 3. Renders each overlay's layers as vector tiles on the map
 * 4. Renders georeferenced images as Mapbox image sources
 *
 * This is separate from MapContent to keep overlay rendering independent from
 * the main project layers, allowing overlays to be displayed without affecting
 * the Legend.
 */
const OverlayMapContent: React.FC<IProps> = ({ layerView }: IProps) => {
  const versionId = useSelector((state: Store) => state.project.versionId);
  const project = useSelector((state: Store) => getCurrentProject(state.project));

  const { overlays: mergedConfig, overlayMetadata, georeferencedImages, isLoading } = useOverlaysWithConfigs(versionId, project);

  const checkVisibility = (id: string, view: { [layerId: string]: boolean }) => {
    if (!mergedConfig) return false;
    return isVisible(mergedConfig, { id, layerView: view });
  };

  // Don't render anything if overlays are still loading or if there are no overlays
  if (isLoading || !mergedConfig) {
    return null;
  }

  // Extract styles from the merged overlay configuration
  const overlayStyles: LayerStyle[] = Object.values(mergedConfig.Data.entities).filter((entity): entity is LayerStyle => entity?.Type === "STYLE");

  // First get the parent "Overlays" group, then extract its children (the actual overlay groups)
  const rootGroup = mergedConfig.MapChildren.map((groupId: string) => mergedConfig.Data.entities[groupId]).find(
    (group): group is GroupConfig => group?.Type === "GROUP"
  );

  // The rootGroup contains a group for each overlay, which we need to extract and render.
  // Filter out the georef-parent-group since those are rendered separately as image layers.
  const overlayGroups = rootGroup?.Children
    ? rootGroup.Children.map((childId: string) => mergedConfig.Data.entities[childId]).filter(
        (group): group is GroupConfig => group?.Type === "GROUP" && group.ID !== "georef-parent-group"
      )
    : [];

  return (
    <Box data-testid="overlay-map-content">
      {overlayGroups.reverse().map((group) => {
        if (!group || group.Type !== "GROUP") return null;

        // Get the overlay metadata for this group
        const metadata = overlayMetadata[group.ID];
        if (!metadata) {
          console.warn(`No metadata found for overlay group ${group.ID}`);
          return null;
        }

        const { overlayId, sourceVersionId } = metadata;

        // Get all layers that belong to this overlay group in the correct rendering order
        // orderLayersByConfiguration will recursively traverse nested groups and return
        // all layers and sublayers in the proper order for Mapbox rendering
        const groupLayers = orderLayersByConfiguration({
          ID: group.ID,
          Key: group.Key ?? "",
          SourceID: mergedConfig.SourceID || "",
          Type: "MapLayerConfig" as const,
          Data: mergedConfig.Data,
          MapChildren: group.Children || [],
        });

        if (groupLayers.length === 0) return null;

        // Create a unique source ID for this overlay group
        const sourceId = `overlay-${overlayId}`;

        return (
          <SourceAndLayers
            key={sourceId}
            sourceId={sourceId}
            source={{
              type: "vector",
              tiles: [makeVersionVectorTileURL(sourceVersionId)],
              promoteId: "id",
              maxzoom: 18,
            }}
            layers={groupLayers}
            styles={overlayStyles}
            layerVisibilities={layerView}
            isVisible={checkVisibility}
            versionId={sourceVersionId}
            // Overlays are actually Underlays in the sense that they are
            // drawn on top of the base map but below the main project layers
            beforeId={BASE_MAP_CUSTOM_OVERLAY_MARKER}
          />
        );
      })}
      {georeferencedImages.map((image) => {
        const entityId = `georef-image-${image.ID}`;
        const imageVisible = checkVisibility(entityId, layerView);
        return <GeorefImageLayer key={entityId} image={image} visible={imageVisible} />;
      })}
    </Box>
  );
};

export default OverlayMapContent;
